// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/users/UserManagementPanel.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../../design-system/components";
import AssignRoleModal from "../roles/AssignRoleModal";
import InvitationQueue from "./InvitationQueue";
import InviteUserModal from "./InviteUserModal";
import UserDetailDrawer from "./UserDetailDrawer";
import UserLifecycleModal from "./UserLifecycleModal";
import UserTable from "./UserTable";

function readableError(error) {
  const raw = String(
    error?.message ||
      error ||
      "The administrative action could not be completed."
  );

  try {
    const parsed = JSON.parse(raw);
    return parsed?.message || parsed?.code || raw;
  } catch {
    return raw;
  }
}

function roleActionNotice(error, roleKey) {
  const raw = String(error?.message || error || "");
  let code = "";
  let message = readableError(error);

  try {
    const parsed = JSON.parse(raw);
    code = parsed?.code || "";
    message = parsed?.message || message;
  } catch {
    code = raw.includes("final_active_owner_required")
      ? "final_active_owner_required"
      : "";
  }

  if (code === "final_active_owner_required") {
    return {
      tone: "warning",
      title: "Owner assignment cannot be removed",
      message:
        "Assign Owner to another active member before removing this assignment.",
      roleKey,
    };
  }

  return {
    tone: "error",
    title: `${String(roleKey || "Role")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase())} could not be removed`,
    message,
    roleKey,
  };
}

export default function UserManagementPanel({
  users = [],
  invitations = [],
  roles = [],
  currentUserId,
  actions = {},
  saving = false,
}) {
  const [selectedUserId, setSelectedUserId] = useState(
    users[0]?.user_id || null
  );
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [removedLoading, setRemovedLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [invitationAction, setInvitationAction] = useState({
    invitationId: null,
    action: null,
  });
  const [lifecycle, setLifecycle] = useState({
    open: false,
    mode: null,
    user: null,
  });
  const [actionError, setActionError] = useState(null);
  const [roleNotice, setRoleNotice] = useState(null);
  const actionsRef = useRef(actions);

  useEffect(() => {
    actionsRef.current = actions;
  }, [actions]);

  useEffect(() => {
    if (!roleNotice) return undefined;

    const timeoutId = window.setTimeout(() => {
      setRoleNotice(null);
    }, 9000);

    return () => window.clearTimeout(timeoutId);
  }, [roleNotice]);

  useEffect(() => {
    if (!users.length) {
      setSelectedUserId(null);
      setSelectedDetail(null);
      return;
    }

    if (!users.some((user) => user.user_id === selectedUserId)) {
      setSelectedUserId(users[0].user_id);
      setSelectedDetail(null);
    }
  }, [users, selectedUserId]);

  const selectedSummary = useMemo(
    () =>
      users.find((user) => user.user_id === selectedUserId) ||
      users[0] ||
      null,
    [users, selectedUserId]
  );

  const selectedUser =
    selectedDetail?.user_id === selectedUserId
      ? selectedDetail
      : selectedSummary;

  const loadDetail = useCallback(async (targetUserId) => {
    const getUserDetail = actionsRef.current?.getUserDetail;

    if (!targetUserId || typeof getUserDetail !== "function") {
      return null;
    }

    setDetailLoading(true);

    try {
      const detail = await getUserDetail(targetUserId);
      setSelectedDetail(detail);
      return detail;
    } catch (error) {
      setActionError(readableError(error));
      return null;
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDetail(selectedUserId);
  }, [selectedUserId, loadDetail]);

  const selectUser = (user) => {
    setActionError(null);
    setRoleNotice(null);
    setSelectedUserId(user.user_id);
    setSelectedDetail(null);
  };

  const pendingCount = invitations.filter(
    (invite) => invite.status === "pending"
  ).length;
  const activeCount = users.filter(
    (user) => user.status === "active"
  ).length;
  const suspendedCount = users.filter(
    (user) => user.status === "suspended"
  ).length;

  const toggleRemovedUsers = async () => {
    const nextValue = !showRemoved;
    setRemovedLoading(true);
    setActionError(null);

    try {
      await actions.listUsers?.(nextValue);
      setShowRemoved(nextValue);
      setSelectedDetail(null);
    } catch (error) {
      setActionError(readableError(error));
    } finally {
      setRemovedLoading(false);
    }
  };

  const handleInvite = async (payload) => {
    setInviteSaving(true);
    setInviteError(null);

    try {
      await actions.invite?.(payload);
      setInviteOpen(false);
      return true;
    } catch (error) {
      setInviteError(readableError(error));
      return false;
    } finally {
      setInviteSaving(false);
    }
  };

  const handleAssignRole = async (targetUserId, roleKey) => {
    setActionError(null);
    setRoleNotice(null);

    try {
      await actions.assignRole?.(targetUserId, roleKey);
      await loadDetail(targetUserId);
      return true;
    } catch (error) {
      setActionError(readableError(error));
      throw error;
    }
  };

  const handleRemoveRole = async (targetUserId, roleKey) => {
    setActionError(null);
    setRoleNotice(null);

    try {
      await actions.removeRole?.(targetUserId, roleKey);
      await loadDetail(targetUserId);
      return true;
    } catch (error) {
      setRoleNotice(roleActionNotice(error, roleKey));
      return false;
    }
  };

  const runInvitationAction = async (action, invitationId) => {
    setInvitationAction({ invitationId, action });
    setActionError(null);

    try {
      if (action === "resend") {
        await actions.resendInvitation?.(invitationId);
      }

      if (action === "cancel") {
        await actions.cancelInvitation?.(invitationId);
      }
    } catch (error) {
      setActionError(readableError(error));
    } finally {
      setInvitationAction({ invitationId: null, action: null });
    }
  };

  const confirmLifecycle = async (reason) => {
    const { mode, user } = lifecycle;
    if (!user || !mode) return;

    setActionError(null);

    try {
      if (mode === "suspend") {
        await actions.suspendUser?.(user.user_id, reason);
      }

      if (mode === "reactivate") {
        await actions.reactivateUser?.(user.user_id, reason);
      }

      if (mode === "remove") {
        await actions.removeUser?.(user.user_id, reason);
      }

      setLifecycle({ open: false, mode: null, user: null });
      setSelectedDetail(null);
      await actions.listUsers?.(showRemoved);
    } catch (error) {
      setActionError(readableError(error));
    }
  };

  return (
    <section className="enterprise-admin-section-panel enterprise-admin-users-panel">
      {roleNotice ? (
        <div
          className={`enterprise-admin-toast ${
            roleNotice.tone === "warning"
              ? "enterprise-admin-toast-warning"
              : "enterprise-admin-toast-error"
          }`}
          role="alert"
          aria-live="assertive"
        >
          <div className="enterprise-admin-toast-icon" aria-hidden="true">
            {roleNotice.tone === "warning" ? "!" : "×"}
          </div>
          <div className="enterprise-admin-toast-content">
            <strong>{roleNotice.title}</strong>
            <p>{roleNotice.message}</p>
          </div>
          <button
            type="button"
            className="enterprise-admin-toast-dismiss"
            aria-label="Dismiss notification"
            onClick={() => setRoleNotice(null)}
          >
            ×
          </button>
        </div>
      ) : null}

      <div className="enterprise-admin-section-header">
        <p className="enterprise-admin-eyebrow">Member access</p>
        <h2>Organization members</h2>
        <p>
          Manage membership, roles, invitations, and audited access changes for this organization.
        </p>
        <Button
          type="button"
          variant="primary"
          onClick={() => {
            setInviteError(null);
            setInviteOpen(true);
          }}
          disabled={saving || inviteSaving}
        >
          Invite member
        </Button>
      </div>

      {actionError && !lifecycle.open ? (
        <div className="enterprise-admin-inline-error" role="alert">
          <strong>Administration action needs attention</strong>
          <span>{actionError}</span>
          <button type="button" onClick={() => setActionError(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="enterprise-admin-metric-grid enterprise-admin-compact-metrics">
        <div className="enterprise-admin-metric-card">
          <span>Active members</span>
          <strong>{activeCount}</strong>
        </div>
        <div className="enterprise-admin-metric-card">
          <span>Pending invitations</span>
          <strong>{pendingCount}</strong>
        </div>
        <div className="enterprise-admin-metric-card">
          <span>Suspended members</span>
          <strong>{suspendedCount}</strong>
        </div>
        <div className="enterprise-admin-metric-card">
          <span>Assignable roles</span>
          <strong>
            {roles.filter((role) => role.is_assignable !== false).length}
          </strong>
        </div>
      </div>

      <div className="enterprise-admin-two-column enterprise-admin-users-workspace">
        <div className="enterprise-admin-card-stack">
          <UserTable
            users={users}
            selectedUserId={selectedUser?.user_id}
            loadingUserId={detailLoading ? selectedUserId : null}
            showRemoved={showRemoved}
            removedLoading={removedLoading}
            onToggleRemoved={toggleRemovedUsers}
            onSelectUser={selectUser}
          />

          <InvitationQueue
            invitations={invitations}
            invitationAction={invitationAction}
            onResend={(invitationId) =>
              runInvitationAction("resend", invitationId)
            }
            onCancel={(invitationId) =>
              runInvitationAction("cancel", invitationId)
            }
          />
        </div>

        <UserDetailDrawer
          user={selectedUser}
          roles={roles}
          loading={detailLoading}
          saving={saving}
          currentUserId={currentUserId}
          onAssignRole={(user) => {
            setSelectedUserId(user.user_id);
            setActionError(null);
            setRoleNotice(null);
            setAssignOpen(true);
          }}
          onRemoveRole={handleRemoveRole}
          onLifecycleAction={(mode, user) => {
            setActionError(null);
            setRoleNotice(null);
            setLifecycle({ open: true, mode, user });
          }}
        />
      </div>

      <InviteUserModal
        open={inviteOpen}
        roles={roles}
        saving={inviteSaving}
        error={inviteError}
        onClose={() => {
          setInviteError(null);
          setInviteOpen(false);
        }}
        onInvite={handleInvite}
      />

      <AssignRoleModal
        open={assignOpen}
        user={selectedUser}
        roles={roles}
        saving={saving}
        onClose={() => setAssignOpen(false)}
        onAssign={handleAssignRole}
      />

      <UserLifecycleModal
        open={lifecycle.open}
        mode={lifecycle.mode}
        user={lifecycle.user}
        saving={saving}
        error={actionError}
        onClose={() => {
          setActionError(null);
          setLifecycle({ open: false, mode: null, user: null });
        }}
        onConfirm={confirmLifecycle}
      />
    </section>
  );
}
